import { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, MapPin, MessageSquare, Send, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Booking modal: lets a client request a date + time slot for a talent (model or professional).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - talentType: 'model' | 'professional'
 *  - talentId: string
 *  - talentName: string (display only)
 */
const BookingModal = ({ open, onClose, talentType, talentId, talentName }) => {
  const { toast } = useToast();
  const [timeSlots, setTimeSlots] = useState([]);
  const [unavailable, setUnavailable] = useState([]); // [{booking_date, time_slot, status}]
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    booking_date: '',
    time_slot: '',
    project_type: '',
    location: '',
    message: '',
  });

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/bookings/time-slots`).then(r => setTimeSlots(r.data.time_slots));
    axios
      .get(`${API}/bookings/availability/${talentType}/${talentId}`)
      .then(r => setUnavailable(r.data.unavailable || []))
      .catch(() => setUnavailable([]));
    setSuccess(false);
    setForm({
      client_name: '',
      client_email: '',
      client_phone: '',
      booking_date: '',
      time_slot: '',
      project_type: '',
      location: '',
      message: '',
    });
  }, [open, talentType, talentId]);

  if (!open) return null;

  // For the picked date, list slots that are already confirmed
  const slotsTakenOnDate = unavailable
    .filter(b => b.booking_date === form.booking_date && b.status === 'confirmed')
    .map(b => b.time_slot);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value, ...(name === 'booking_date' ? { time_slot: '' } : {}) }));
  };

  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        talent_type: talentType,
        talent_id: talentId,
        ...form,
      });
      setSuccess(true);
      toast({
        title: 'Booking Request Sent!',
        description: `We'll email you when ${talentName}'s booking is confirmed.`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to submit booking. Please try again.';
      toast({ title: 'Booking Failed', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="booking-modal-overlay"
    >
      <div
        className="bg-zinc-900 border border-red-900/40 rounded-xl w-full max-w-lg my-8 shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="booking-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-red-900/30">
          <div>
            <h3 className="text-2xl font-display font-bold text-white">Request Booking</h3>
            <p className="text-sm text-gray-400 mt-1">
              with <span className="text-red-400">{talentName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            data-testid="booking-modal-close"
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-4">
              <Send className="text-green-400" size={26} />
            </div>
            <h4 className="text-xl font-semibold text-white mb-2">Request Submitted</h4>
            <p className="text-gray-400 mb-6">
              We've notified the team. You'll receive a confirmation email at{' '}
              <span className="text-red-400">{form.client_email}</span> shortly.
            </p>
            <button
              onClick={onClose}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-full font-mono text-sm tracking-widest uppercase transition-colors"
              data-testid="booking-success-close"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="booking-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Your Name" icon={User} required>
                <input
                  type="text"
                  name="client_name"
                  required
                  value={form.client_name}
                  onChange={handleChange}
                  className="input"
                  data-testid="booking-input-name"
                />
              </Field>
              <Field label="Email" icon={Mail} required>
                <input
                  type="email"
                  name="client_email"
                  required
                  value={form.client_email}
                  onChange={handleChange}
                  className="input"
                  data-testid="booking-input-email"
                />
              </Field>
            </div>

            <Field label="Phone" icon={Phone}>
              <input
                type="tel"
                name="client_phone"
                value={form.client_phone}
                onChange={handleChange}
                className="input"
                data-testid="booking-input-phone"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Preferred Date" icon={Calendar} required>
                <input
                  type="date"
                  name="booking_date"
                  required
                  min={today}
                  value={form.booking_date}
                  onChange={handleChange}
                  className="input [color-scheme:dark]"
                  data-testid="booking-input-date"
                />
              </Field>
              <Field label="Time Slot" icon={Clock} required>
                <select
                  name="time_slot"
                  required
                  value={form.time_slot}
                  onChange={handleChange}
                  className="input"
                  disabled={!form.booking_date}
                  data-testid="booking-input-slot"
                >
                  <option value="">{form.booking_date ? 'Select a slot' : 'Pick a date first'}</option>
                  {timeSlots.map(slot => {
                    const taken = slotsTakenOnDate.includes(slot);
                    return (
                      <option key={slot} value={slot} disabled={taken}>
                        {slot}
                        {taken ? ' — Booked' : ''}
                      </option>
                    );
                  })}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Project Type">
                <input
                  type="text"
                  name="project_type"
                  placeholder="e.g. Editorial shoot"
                  value={form.project_type}
                  onChange={handleChange}
                  className="input"
                  data-testid="booking-input-project"
                />
              </Field>
              <Field label="Location" icon={MapPin}>
                <input
                  type="text"
                  name="location"
                  placeholder="Studio / on location"
                  value={form.location}
                  onChange={handleChange}
                  className="input"
                  data-testid="booking-input-location"
                />
              </Field>
            </div>

            <Field label="Message" icon={MessageSquare}>
              <textarea
                name="message"
                rows={3}
                placeholder="Tell us about the project, references, budget, etc."
                value={form.message}
                onChange={handleChange}
                className="input resize-none"
                data-testid="booking-input-message"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2 mt-2"
              data-testid="booking-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Sending...
                </>
              ) : (
                <>
                  Send Booking Request <Send size={16} />
                </>
              )}
            </button>

            <style>{`
              .input {
                width: 100%;
                background: #000;
                border: 1px solid rgba(127, 29, 29, 0.4);
                border-radius: 8px;
                padding: 10px 14px;
                color: #fff;
                font-size: 14px;
                outline: none;
                transition: border-color 0.15s ease;
              }
              .input:focus { border-color: #ef4444; }
              .input:disabled { opacity: 0.5; cursor: not-allowed; }
              .input::placeholder { color: #6b7280; }
            `}</style>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, icon: Icon, required, children }) => (
  <label className="block">
    <span className="flex items-center gap-2 text-xs font-mono text-gray-400 mb-1.5 tracking-wider uppercase">
      {Icon ? <Icon size={12} /> : null} {label}
      {required ? ' *' : ''}
    </span>
    {children}
  </label>
);

export default BookingModal;
